import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material'
import { useMemo, useState } from 'react'

import { COMBOBOX_FILTER_COMPACT, HIDE_ON_MOBILE } from '@/theme/responsive'

import { useGetPurchaseReferenceRowsQuery } from '../../store/api/purchaseContractApi'

import PurchaseReferenceGrid from './PurchaseReferenceGrid'
import {
  PURCHASE_REFERENCE_PAGE_CONFIGS,
  PURCHASE_REFERENCE_PAGE_ORDER,
  purchaseReferenceSheetColumnsMode,
  type PurchaseReferencePageKey,
} from './purchaseReferenceConfig'

export default function PurchaseReferencesView() {
  const [pageKey, setPageKey] = useState<PurchaseReferencePageKey>('ValueUpdates')
  const [searchByPage, setSearchByPage] = useState<Partial<Record<PurchaseReferencePageKey, string>>>({})

  const page = PURCHASE_REFERENCE_PAGE_CONFIGS[pageKey]
  const search = searchByPage[pageKey] ?? ''

  const queryArg = useMemo(
    () => ({
      pageKey,
      limit: page.limit ?? 5000,
    }),
    [page.limit, pageKey],
  )

  const { data, isLoading, error, refetch } = useGetPurchaseReferenceRowsQuery(queryArg)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, flex: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 0.25 }}
      >
        <Box sx={HIDE_ON_MOBILE}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.4 }}>
            Purchase References
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Access switchboard parity for the 12 Purchase reference pages.
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5} sx={{ ml: 'auto' }}>
          {(() => {
            // Skill /convert-form: combo → Autocomplete (anti-pattern #12 cấm
            // <TextField select>). PageKey là code internal nên dropdown chỉ
            // display label (không format "<CODE> — <NAME>" vì code không
            // user-meaningful — pattern hardcoded enum trong skill).
            const pageOptions = PURCHASE_REFERENCE_PAGE_ORDER.map((key) => ({
              value: key,
              label: PURCHASE_REFERENCE_PAGE_CONFIGS[key].label,
            }))
            const selectedOption = pageOptions.find((o) => o.value === pageKey) ?? undefined
            return (
              <Autocomplete
                size="small"
                options={pageOptions}
                value={selectedOption}
                onChange={(_, v) => v && setPageKey(v.value)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                handleHomeEndKeys
                autoHighlight
                selectOnFocus
                sx={COMBOBOX_FILTER_COMPACT}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Go to Page:"
                    InputLabelProps={{ shrink: true }}
                    // readOnly trên <input>: mobile KHÔNG bung keyboard khi tap
                    // (user chỉ pick từ dropdown, không cần gõ). Autocomplete
                    // dropdown vẫn mở qua click bình thường.
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            )
          })()}
        </Stack>
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <PurchaseReferenceGrid
          page={page}
          columnsMode={purchaseReferenceSheetColumnsMode(pageKey)}
          rows={data?.items ?? []}
          search={search}
          onSearchChange={(value) =>
            setSearchByPage((prev) => ({
              ...prev,
              [pageKey]: value,
            }))
          }
          loading={isLoading}
          error={error}
          onRetry={() => {
            void refetch()
          }}
        />
      </Box>
    </Box>
  )
}
